// Favourite library objects: the ids of the objects the person starred, kept in THIS browser
// (localStorage). Nothing goes to Eden. Pure, so the tests hold it; storage is passed in.
//
// A favourite is an id. An id with no object behind it (the object was removed, or the account
// library is not loaded yet) is KEPT in storage and ignored on screen, so a favourite does not
// vanish while the account library is out of reach.

export const FAVS_KEY = "tracecad.library.favs";

/** A list longer than this is cut. It protects the storage from a corrupt or hostile value. */
export const MAX_FAVS = 5000;

/** The part of the Storage interface used here. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

/** The browser's localStorage, or null when the browser refuses it (private window, blocked data). */
export function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Read a stored value. Anything that is not a JSON array of non-empty strings gives []. */
export function parseFavs(raw: string | null | undefined): string[] {
  if (!raw) return [];
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  for (const x of v) {
    if (typeof x === "string" && x !== "" && !seen.has(x)) {
      seen.add(x);
      if (seen.size >= MAX_FAVS) break;
    }
  }
  return [...seen];
}

export function serialiseFavs(ids: Iterable<string>): string {
  return JSON.stringify([...new Set(ids)].filter((x) => typeof x === "string" && x !== "").slice(0, MAX_FAVS));
}

/** Add the id when it is not in the list, remove it when it is. Gives a new list. */
export function toggleFav(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

export function removeFav(ids: readonly string[], id: string): string[] {
  return ids.filter((x) => x !== id);
}

export function loadFavs(storage: StorageLike | null = browserStorage()): string[] {
  if (!storage) return [];
  try {
    return parseFavs(storage.getItem(FAVS_KEY));
  } catch {
    return [];
  }
}

/** True when the list was written. False when the browser refused (the favourites last until the page closes). */
export function saveFavs(ids: readonly string[], storage: StorageLike | null = browserStorage()): boolean {
  if (!storage) return false;
  try {
    storage.setItem(FAVS_KEY, serialiseFavs(ids));
    return true;
  } catch {
    return false;
  }
}
